import {
    DEFAULT_EMPTY_Y_AXIS_TICKS,
    DEFAULT_Y_AXIS_DESIRED_STEPS,
    LEVEL3_SMALL_RANGE_Y_AXIS,
    Y_AXIS_DESIRED_STEPS_BY_LEVEL,
} from "../chart-constants";
import { type AreaData, type AreaKey, type AreaNavItem, extendedData } from "./data-config";

export type EmissionsSeriesKey = "klimamal" | "utenEnova" | "medEnova" | "forventetUtvikling";

export const EMISSIONS_LINE_LABELS: Record<EmissionsSeriesKey, string> = {
    klimamal: "Indikativ utslippsbane", // tidligere Norges klimamål
    utenEnova: "Forventet utvikling uten Enova",
    medEnova: "Forventet utvikling med Enova",
    forventetUtvikling: "Forventet utvikling",
};

export const EMISSIONS_UNIT = "Mt CO₂e";

const AREA_KEYS: AreaKey[] = ["area1", "area2", "area3", "area4", "area5"];

export const RADIO_VIEW_VALUES = {
    EMISSIONS: "utslipp",
    MARKET: "markedsutvikling",
    ENOVA_ACCUMULATED: "enova-akkumulert",
    ENOVA_YEARLY: "enova-arlig",
} as const;

export type RadioViewValue = (typeof RADIO_VIEW_VALUES)[keyof typeof RADIO_VIEW_VALUES];

export const formatNumberToTwoDecimals = (num: number) => {
    if (typeof num !== "number" || Number.isNaN(num)) {
        return "0,00";
    }

    return num.toLocaleString("nb-NO", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

export const getNavItemValueAndPercentage = (year: number, areaKey: AreaKey, data?: AreaData[]) => {
    const dataSource = data || extendedData;
    const yearData = dataSource.find((d) => d.year === year);
    if (!yearData) return "";
    const value = yearData[areaKey];
    const total = AREA_KEYS.reduce((sum, key) => sum + (yearData[key] ?? 0), 0);
    const percent = total > 0 ? (value / total) * 100 : undefined;
    const percentLabel = percent !== undefined && percent < 100 ? ` (${percent.toFixed(1).replace(".", ",")}%)` : "";
    return `${formatNumberToTwoDecimals(value)} ${EMISSIONS_UNIT}${percentLabel}`;
};

export const getNavItemValue = (year: number, areaKey: AreaKey, data?: AreaData[]): number => {
    const dataSource = data || extendedData;
    const yearData = dataSource.find((d) => d.year === year);
    if (!yearData) return 0;

    const value = yearData[areaKey];

    return value;
};

export const sortNavItemsByEmissionsDesc = (year: number, navItems: AreaNavItem[], data?: AreaData[]) => {
    const sortedItems = [...navItems];

    sortedItems.sort((itemA, itemB) => {
        const diff = getNavItemValue(year, itemB.area, data) - getNavItemValue(year, itemA.area, data);
        if (diff !== 0) {
            return diff;
        }

        return itemA.label.localeCompare(itemB.label, "nb-NO");
    });

    return sortedItems;
};

export const getTotalForYear = (year: number, data?: AreaData[]) => {
    const dataSource = data || extendedData;
    const yearData = dataSource.find((d) => d.year === year);
    if (!yearData) return 0;

    return AREA_KEYS.reduce((sum, key) => sum + (yearData[key] ?? 0), 0);
};

const toSafeNumber = (value: number | undefined | null) => (Number.isFinite(value) ? (value as number) : 0);

const getAreaTotal = (data: AreaData) => AREA_KEYS.reduce((sum, key) => sum + toSafeNumber(data[key]), 0);

const getEffectiveMax = (chartData: AreaData[]) => {
    const areaTotals = chartData.map(getAreaTotal);
    const areaMax = areaTotals.length > 0 ? Math.max(...areaTotals) : 0;
    const lineMax = chartData.reduce((max, d) => {
        const uten = toSafeNumber(d.utenEnova);
        const goal = toSafeNumber(d.klimamal);
        return Math.max(max, uten, goal);
    }, 0);
    const effectiveMax = Math.max(areaMax, lineMax);
    return Number.isFinite(effectiveMax) ? effectiveMax : 0;
};

const createNiceTicks = (maxVal: number, desiredSteps = 5) => {
    if (!Number.isFinite(maxVal) || maxVal <= 0) {
        return [...DEFAULT_EMPTY_Y_AXIS_TICKS];
    }

    const roughStep = maxVal / desiredSteps;
    const magnitude = 10 ** Math.floor(Math.log10(roughStep));
    const normalized = roughStep / magnitude;

    let niceFactor = 1;
    if (normalized <= 1) {
        niceFactor = 1;
    } else if (normalized <= 2) {
        niceFactor = 2;
    } else if (normalized <= 5) {
        niceFactor = 5;
    } else {
        niceFactor = 10;
    }

    const tickInterval = niceFactor * magnitude;
    const roundedMax = Math.ceil(maxVal / tickInterval) * tickInterval;
    const ticks: number[] = [];
    for (let value = 0; value <= roundedMax + tickInterval / 2; value += tickInterval) {
        ticks.push(Math.round(value * 100) / 100);
    }

    return ticks.length > 0 ? ticks : [0];
};

export const getYAxisTicks = (chartData: AreaData[], level: number) => {
    const effectiveMax = getEffectiveMax(chartData);
    if (level === 3 && effectiveMax < LEVEL3_SMALL_RANGE_Y_AXIS.max) {
        return [...LEVEL3_SMALL_RANGE_Y_AXIS.ticks];
    }

    const desiredSteps = Y_AXIS_DESIRED_STEPS_BY_LEVEL[level] ?? DEFAULT_Y_AXIS_DESIRED_STEPS;
    return createNiceTicks(effectiveMax, desiredSteps);
};

export const getYAxisDomain = (chartData: AreaData[], level: number): [number, number] | [number, string] => {
    const ticks = getYAxisTicks(chartData, level);
    const domainMax = ticks[ticks.length - 1];
    if (Number.isFinite(domainMax) && domainMax > 0) {
        return [0, domainMax];
    }
    return [0, "dataMax"];
};

export function getRadioOptionsLevel4(): { value: RadioViewValue; label: string }[] {
    return [
        { value: RADIO_VIEW_VALUES.EMISSIONS, label: "Utslipp" },
        { value: RADIO_VIEW_VALUES.MARKET, label: "Markedsutvikling" },
        { value: RADIO_VIEW_VALUES.ENOVA_ACCUMULATED, label: "Enovas aktivitet, samlet" },
        { value: RADIO_VIEW_VALUES.ENOVA_YEARLY, label: "Enovas aktivitet, år for år" },
    ];
}
